import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VBackdrop from "@/components/VBackdrop";
import HomePage from "@/pages/HomePage";
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const IndustryPage = lazy(() => import("@/pages/IndustryPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const InsightsPage = lazy(() => import("@/pages/InsightsPage"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const SecurityPage = lazy(() => import("@/pages/SecurityPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
import { getService } from "@/data/services";
import { getIndustry } from "@/data/industries";
import { getArticle } from "@/data/insights";
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
import ProtectedRoute from "@/components/ProtectedRoute";
import ThemeGate from "@/components/ThemeGate";
import { setLenis, getLenis } from "@/lib/scroll";
import { setTheme, hasStoredTheme } from "@/lib/theme";
import { toast } from "sonner";

const SITE_URL = "https://www.varasuntechnologies.com";

const ScrollManager = () => {
    const location = useLocation();
    useEffect(() => {
        if (!location.hash) {
            const lenis = getLenis();
            if (lenis) lenis.scrollTo(0, { immediate: true });
            else window.scrollTo(0, 0);
        }
    }, [location.pathname, location.hash]);
    return null;
};

const SmoothScroll = ({ children }) => {
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
        setLenis(lenis);
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            setLenis(null);
        };
    }, []);
    return children;
};

const MainRoutes = () => {
    return (
        <Suspense fallback={<div className="min-h-screen t-bg" aria-hidden="true" />}>
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/industries/:slug" element={<IndustryPage />} />
                <Route path="/services/:slug" element={<ServicePage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/insights" element={<InsightsPage />} />
                <Route path="/insights/:slug" element={<ArticlePage />} />
                <Route path="/security" element={<SecurityPage />} />
                <Route path="/privacy" element={<LegalPage type="privacy" />} />
                <Route path="/terms" element={<LegalPage type="terms" />} />
                <Route path="/login" element={<AdminLogin />} />
                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute>{(user) => <AdminLeads user={user} />}</ProtectedRoute>
                    }
                />
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </Suspense>
    );
};

const RouteMeta = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        const seg = pathname.split("/");
        let title = null;
        let desc =
            "Varasun helps enterprises modernize technology, strengthen security, unlock data and AI capabilities, and build resilient digital operations.";
        if (pathname === "/") title = "Transforming Technology Into Business Advantage";
        else if (seg[1] === "about") title = "About Varasun";
        else if (seg[1] === "contact") title = "Talk to an Expert";
        else if (seg[1] === "careers") title = "Careers at Varasun";
        else if (seg[1] === "security") title = "Security & Trust";
        else if (seg[1] === "privacy") title = "Privacy Policy";
        else if (seg[1] === "terms") title = "Terms of Use";
        else if (seg[1] === "insights" && seg[2]) {
            const a = getArticle(seg[2]);
            if (a) {
                title = a.title;
                desc = a.excerpt;
            }
        } else if (seg[1] === "insights") title = "Insights";
        else if (seg[1] === "services" && seg[2]) {
            const s = getService(seg[2]);
            if (s) {
                title = s.name;
                desc = s.subhead;
            }
        } else if (seg[1] === "industries" && seg[2]) {
            const ind = getIndustry(seg[2]);
            if (ind) {
                title = `${ind.name} Technology Services`;
                desc = ind.subhead;
            }
        } else if (seg[1] === "login" || seg[1] === "admin") title = "Admin Console";
        if (title) {
            document.title = `${title} | Varasun Technologies`;
            const meta = document.querySelector('meta[name="description"]');
            if (meta) meta.setAttribute("content", desc);
        }
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute("href", `${SITE_URL}${pathname === "/" ? "/" : pathname}`);
    }, [pathname]);
    return null;
};

const Chrome = ({ children }) => {
    const { pathname } = useLocation();
    const bare = pathname.startsWith("/admin") || pathname.startsWith("/login");
    const [showGate, setShowGate] = useState(() => !hasStoredTheme());

    useEffect(() => {
        const lenis = getLenis();
        if (showGate && !bare) {
            lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            lenis?.start();
            document.body.style.overflow = "";
        }
    }, [showGate, bare]);

    const chooseTheme = (t) => {
        setTheme(t);
        setShowGate(false);
        toast.success(t === "dark" ? "Dark theme saved" : "Light theme saved", {
            description: "Your preference is remembered on this device.",
            duration: 2600,
        });
    };

    return (
        <div className={`min-h-screen font-body ${bare ? "bg-navy-950 text-silver" : "t-text"}`}>
            <RouteMeta />
            {!bare && <VBackdrop />}
            {!bare && <Navbar />}
            {!bare && <span id="main-content" tabIndex={-1} />}
            {children}
            {!bare && <Footer />}
            {!bare && showGate && <ThemeGate onChoose={chooseTheme} />}
        </div>
    );
};

function App() {
    return (
        <BrowserRouter>
            <SmoothScroll>
                <ScrollManager />
                <Chrome>
                    <MainRoutes />
                </Chrome>
                <Toaster position="bottom-right" richColors />
            </SmoothScroll>
        </BrowserRouter>
    );
}

export default App;
